import React, { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Music, ChevronDown } from "lucide-react";
import { T } from "../theme";

const API_URL = process.env.REACT_APP_API_URL || "";


const inputStyle = {
  width: "100%", padding: "12px 16px",
  background: "rgba(255,255,255,0.07)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 10, color: T.textPrimary,
  fontSize: "0.95rem", outline: "none",
  boxSizing: "border-box", fontFamily: "inherit",
};

function SectionLabel({ children }) {
  return (
    <p style={{
      fontSize: "0.75rem", fontWeight: 700, color: T.textMuted,
      textTransform: "uppercase", letterSpacing: "0.1em",
      margin: "0 0 8px",
    }}>
      {children}
    </p>
  );
}

export default function LocationPicker({ value, onChange, compact = false }) {
  const [geoPos, setGeoPos]             = useState(null);
  const [nearbyEvents, setNearbyEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [expanded, setExpanded]          = useState(false);
  const [customMode, setCustomMode]      = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeoPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }, []);

  useEffect(() => {
    if (!geoPos) return;
    setLoadingEvents(true);
    axios.get(`${API_URL}/api/events/nearby?lat=${geoPos.lat}&lng=${geoPos.lng}`)
      .then(res => setNearbyEvents(res.data.events || []))
      .catch(() => {})
      .finally(() => setLoadingEvents(false));
  }, [geoPos]);

  const selectEvent = (event) => {
    onChange({
      location: `${event.name}${event.venue?.name ? ` @ ${event.venue.name}` : ""}`,
      lat: event.venue.lat,
      lng: event.venue.lng,
      eventName: event.name,
      eventLink: event.link || null,
    });
    setExpanded(false);
    setCustomMode(false);
  };

  const handleCustomChange = (text) => {
    onChange({
      location: text,
      lat: geoPos?.lat || null,
      lng: geoPos?.lng || null,
      eventName: null,
    });
  };

  const hasSelection = value?.location && !customMode;

  return (
    <div style={{ marginBottom: compact ? 10 : 22 }}>
      {!compact && <SectionLabel>Location</SectionLabel>}

      {hasSelection && value.eventName && (
        <div style={{
          padding: "10px 14px", borderRadius: 12, marginBottom: 8,
          background: "linear-gradient(135deg, rgba(0,245,255,0.1), rgba(180,79,255,0.1))",
          border: "1px solid rgba(0,245,255,0.3)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MapPin size={14} color={T.accentCyan} />
            <span style={{ flex: 1, color: T.textPrimary, fontSize: "0.88rem", fontWeight: 600 }}>
              {value.location}
            </span>
            <button
              onClick={() => { onChange({ location: "", lat: null, lng: null, eventName: null, eventLink: null }); setCustomMode(false); }}
              style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: 4 }}
            >
              ✕
            </button>
          </div>
          {value.eventLink && (
            <a
              href={value.eventLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: T.accentCyan, fontSize: "0.72rem", textDecoration: "none", marginTop: 4, display: "inline-block" }}
            >
              View on Edmtrain →
            </a>
          )}
        </div>
      )}

      {(!hasSelection || !value.eventName) && (
        <>
          {nearbyEvents.length > 0 && !customMode && (
            <div style={{ marginBottom: 8 }}>
              <button
                onClick={() => setExpanded(!expanded)}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                  padding: "11px 14px", borderRadius: 12,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: T.accentCyan, fontSize: "0.85rem", fontWeight: 600,
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                <Music size={14} />
                {loadingEvents ? "Finding nearby events..." : `${nearbyEvents.length} events nearby`}
                <ChevronDown size={14} style={{
                  marginLeft: "auto",
                  transform: expanded ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 0.2s",
                }} />
              </button>

              {expanded && (
                <div style={{
                  marginTop: 6, borderRadius: 14, overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.08)",
                  maxHeight: 240, overflowY: "auto",
                }}>
                  {nearbyEvents.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => selectEvent(event)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "12px 14px", border: "none", borderBottom: "1px solid rgba(255,255,255,0.05)",
                        background: "rgba(255,255,255,0.03)",
                        cursor: "pointer", textAlign: "left", transition: "background 0.12s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(180,79,255,0.12)"}
                      onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: "linear-gradient(135deg, rgba(180,79,255,0.2), rgba(255,60,172,0.2))",
                        border: "1px solid rgba(180,79,255,0.3)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Music size={14} color={T.accentPurple} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          margin: 0, color: T.textPrimary, fontSize: "0.85rem", fontWeight: 600,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {event.name}
                        </p>
                        <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: "0.72rem" }}>
                          {event.venue?.name}{event.venue?.city ? ` · ${event.venue.city}` : ""}
                          {event.date ? ` · ${new Date(event.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                        </p>
                      </div>
                    </button>
                  ))}
                  <div style={{
                    padding: "8px 14px", background: "rgba(255,255,255,0.02)",
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                    textAlign: "center",
                  }}>
                    <a
                      href="https://edmtrain.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: T.textMuted, fontSize: "0.65rem", textDecoration: "none" }}
                    >
                      Event data powered by Edmtrain
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {!customMode && nearbyEvents.length > 0 && (
            <button
              onClick={() => setCustomMode(true)}
              style={{
                background: "none", border: "none", color: T.textMuted,
                fontSize: "0.78rem", cursor: "pointer", padding: "4px 0",
                textDecoration: "underline", textUnderlineOffset: 3,
              }}
            >
              Or enter a location manually
            </button>
          )}

          {(customMode || nearbyEvents.length === 0) && (
            <div>
              <input
                type="text"
                placeholder={geoPos ? "Type a location name..." : "Enable location for event suggestions, or type here..."}
                value={value?.eventName ? "" : (value?.location || "")}
                onChange={(e) => handleCustomChange(e.target.value)}
                style={inputStyle}
              />
              {customMode && nearbyEvents.length > 0 && (
                <button
                  onClick={() => setCustomMode(false)}
                  style={{
                    background: "none", border: "none", color: T.accentCyan,
                    fontSize: "0.78rem", cursor: "pointer", padding: "6px 0",
                  }}
                >
                  ← Back to event suggestions
                </button>
              )}
              {geoPos && (
                <p style={{ fontSize: "0.72rem", color: T.textMuted, margin: "6px 0 0" }}>
                  <MapPin size={10} style={{ verticalAlign: -1 }} /> GPS coordinates will be saved for the journey map
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
